/**
 * ClaimSync Terms of Service Page
 * Matching homepage design
 */

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function TermsOfServicePage() {
  const lastUpdated = 'December 24, 2024';

  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);
    
    const header = document.querySelector('header');
    const handleScroll = () => {
      if (header) {
        header.style.boxShadow = window.pageYOffset > 100 ? '0 4px 20px rgba(0, 0, 0, 0.3)' : 'none';
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A1628] border-b border-white/10">
        <nav className="max-w-[1400px] mx-auto px-8 lg:px-12 py-5 flex justify-between items-center">
          <Link to="/home" className="flex items-center">
            <img src="/logo.svg" alt="ClaimSync" className="h-44 lg:h-56 w-auto" />
          </Link>
          <ul className="hidden md:flex items-center gap-8 lg:gap-10">
            <li><Link to="/home#features" className="text-white/85 hover:text-white font-medium text-sm tracking-tight transition-colors">Features</Link></li>
            <li><Link to="/about" className="text-white/85 hover:text-white font-medium text-sm tracking-tight transition-colors">About</Link></li>
            <li><Link to="/contact" className="text-white/85 hover:text-white font-medium text-sm tracking-tight transition-colors">Contact</Link></li>
            <li><Link to="/privacy" className="text-white/85 hover:text-white font-medium text-sm tracking-tight transition-colors">Privacy</Link></li>
            <li>
              <Link 
                to="/sign-in" 
                className="bg-[#2762EA] hover:bg-[#1e50c7] text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/20"
              >
                Sign In
              </Link>
            </li>
          </ul>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="pt-64 lg:pt-72 pb-12 bg-[#0A1628]">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
            Terms of Service
          </h1>
          <p className="text-blue-200">Last updated: {lastUpdated}</p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-8 py-16">
        <div className="bg-[#FAFAF9] rounded-2xl p-8 md:p-12 border border-slate-200/50">
          <div className="prose prose-slate max-w-none">
            <Section title="1. Acceptance of Terms">
              <p>
                By accessing or using ClaimSync ("Service"), you agree to be bound by these Terms of Service ("Terms"). 
                If you do not agree to these Terms, you may not use the Service.
              </p>
              <p>
                These Terms apply to all users, including administrators, team members, and any other individuals 
                who access or use the Service.
              </p>
            </Section>

            <Section title="2. Description of Service">
              <p>ClaimSync is a business interruption claim calculation platform that provides:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Standard Business Interruption (BI) calculations</li>
                <li>Financial data analysis and processing</li>
                <li>AI-powered transaction classification</li>
                <li>Monte Carlo risk simulations</li>
                <li>Professional report generation</li>
                <li>Team collaboration features</li>
              </ul>
            </Section>

            <Section title="3. Account Registration">
              <p>To use ClaimSync, you must:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Provide accurate and complete registration information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>Be at least 18 years old or the age of majority in your jurisdiction</li>
              </ul>
              <p className="mt-4">You are responsible for all activities that occur under your account.</p>
            </Section>

            <Section title="4. Acceptable Use">
              <p>You agree NOT to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Use the Service for any unlawful purpose</li>
                <li>Upload false, misleading, or fraudulent data</li>
                <li>Attempt to gain unauthorized access to any part of the Service</li>
                <li>Interfere with or disrupt the Service or its infrastructure</li>
                <li>Reverse engineer, decompile, or disassemble the Service</li>
                <li>Use automated systems to access the Service without permission</li>
                <li>Violate the intellectual property rights of others</li>
                <li>Share account credentials with unauthorized parties</li>
              </ul>
            </Section>

            <Section title="5. Data and Content">
              <h4 className="font-semibold text-[#0A1628] mt-4 mb-2">Your Data</h4>
              <p>
                You retain ownership of all data and content you upload to ClaimSync. By using the Service, 
                you grant us a limited license to process your data solely for the purpose of providing the Service.
              </p>
              
              <h4 className="font-semibold text-[#0A1628] mt-4 mb-2">Data Accuracy</h4>
              <p>
                You are responsible for the accuracy and legality of data you upload. ClaimSync processes 
                data as provided and does not verify the accuracy of input data.
              </p>

              <h4 className="font-semibold text-[#0A1628] mt-4 mb-2">Generated Reports</h4>
              <p>
                Reports generated by ClaimSync are based on the data you provide and standard calculation methodologies. 
                These reports are tools to assist professionals and should be reviewed before use in any legal or formal proceedings.
              </p>
            </Section>

            <Section title="6. Professional Responsibility">
              <p>ClaimSync is a calculation and analysis tool. Users acknowledge that:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>The Service does not provide legal, accounting, or professional advice</li>
                <li>Users are responsible for verifying all calculations and outputs</li>
                <li>Professional judgment should be applied to all generated reports</li>
                <li>Users should consult qualified professionals as appropriate</li>
              </ul>
            </Section>

            <Section title="7. Intellectual Property">
              <p>
                ClaimSync and its original content, features, and functionality are owned by ClaimSync, Inc. 
                and are protected by copyright, trademark, and other intellectual property laws.
              </p>
              <p className="mt-4">
                You may not copy, modify, distribute, sell, or lease any part of the Service without our written permission.
              </p>
            </Section>

            <Section title="8. Service Availability">
              <p>
                We strive to maintain Service availability but do not guarantee uninterrupted access. 
                The Service may be temporarily unavailable due to:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Scheduled maintenance</li>
                <li>System updates and improvements</li>
                <li>Technical issues beyond our control</li>
                <li>Force majeure events</li>
              </ul>
            </Section>

            <Section title="9. Limitation of Liability">
              <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>The Service is provided "AS IS" without warranties of any kind</li>
                <li>We disclaim all warranties, express or implied, including merchantability and fitness for a particular purpose</li>
                <li>We are not liable for any indirect, incidental, special, consequential, or punitive damages</li>
                <li>Our total liability shall not exceed the amount you paid for the Service in the past 12 months</li>
              </ul>
            </Section>

            <Section title="10. Indemnification">
              <p>
                You agree to indemnify and hold harmless ClaimSync, its officers, directors, employees, and agents 
                from any claims, damages, losses, or expenses (including reasonable attorney fees) arising from your 
                use of the Service, violation of these Terms, or infringement of any third-party rights.
              </p>
            </Section>

            <Section title="11. Termination">
              <p>
                We may terminate or suspend your account and access to the Service at our sole discretion, 
                without prior notice, for conduct that we believe:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Violates these Terms</li>
                <li>Is harmful to other users or the Service</li>
                <li>Is fraudulent or illegal</li>
              </ul>
              <p className="mt-4">Upon termination, your right to use the Service will immediately cease.</p>
            </Section>

            <Section title="12. Changes to Terms">
              <p>
                We reserve the right to modify these Terms at any time. We will provide notice of material changes 
                by posting the updated Terms on this page and updating the "Last updated" date. 
                Your continued use of the Service after changes constitutes acceptance of the new Terms.
              </p>
            </Section>

            <Section title="13. Governing Law">
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, 
                United States, without regard to its conflict of law provisions. Any disputes shall be resolved 
                in the courts located in Delaware.
              </p>
            </Section>

            <Section title="14. Contact Information">
              <p>For questions about these Terms, please contact us:</p>
              <div className="bg-white rounded-lg p-4 mt-4 border border-slate-200">
                <p className="text-slate-600 text-sm">
                  <strong>Email:</strong> legal@claimsync.com<br />
                  <strong>Address:</strong> ClaimSync, Inc.<br />
                  <Link to="/contact" className="text-[#2762EA] hover:underline">Contact Form</Link>
                </p>
              </div>
            </Section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0A1628] py-12">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <img src="/logo.svg" alt="ClaimSync" className="h-12 w-auto mb-4" />
              <p className="text-slate-400 text-sm">
                Professional business interruption claim calculations for accountants and legal professionals.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><Link to="/demo" className="text-slate-400 hover:text-white text-sm transition-colors">Demo</Link></li>
                <li><Link to="/home#features" className="text-slate-400 hover:text-white text-sm transition-colors">Features</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-slate-400 hover:text-white text-sm transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="text-slate-400 hover:text-white text-sm transition-colors">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="text-slate-400 hover:text-white text-sm transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-slate-400 hover:text-white text-sm transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} ClaimSync. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-xl font-bold text-[#0A1628] mb-4">{title}</h2>
      <div className="text-slate-600 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}
